import { defineStore } from 'pinia'
import { ref, type Ref, watch } from 'vue'
import type { GameOptions, Categories } from '@/types'

const STORAGE_KEY = 'guess-game-config'

const DEFAULT_OPTIONS: GameOptions = {
  difficulty: 'medio',
  categoryMode: 'roulette',
  selectedCategories: [],
  roundsPerPlayer: 1,
  timerDuration: 60,
  connectionMode: 'internet'
}

// Cargar configuración desde localStorage
const loadFromStorage = (): GameOptions => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      const parsed = JSON.parse(saved)
      return { ...DEFAULT_OPTIONS, ...parsed }
    }
  } catch (e) {
    console.error('Error loading config from localStorage:', e)
  }

  // MIGRATION: If guess-game-config doesn't exist but connectionType does,
  // use the old connectionType value as the default
  const oldConnectionType = localStorage.getItem('connectionType') as 'internet' | 'lan' | null
  if (oldConnectionType) {
    console.log('[gameConfig] Migrating connectionType from old storage:', oldConnectionType)
    // Map 'lan' → 'local', 'internet' → 'internet'
    const migratedMode: 'internet' | 'local' = oldConnectionType === 'internet' ? 'internet' : 'local'
    const migratedOptions = { ...DEFAULT_OPTIONS, connectionMode: migratedMode }
    // Save the migrated config
    localStorage.setItem(STORAGE_KEY, JSON.stringify(migratedOptions))
    // Optionally remove the old key
    // localStorage.removeItem('connectionType')
    return migratedOptions
  }

  return { ...DEFAULT_OPTIONS }
}

export const useGameConfigStore = defineStore('gameConfig', () => {
  // Cargar desde localStorage al iniciar
  const options: Ref<GameOptions> = ref(loadFromStorage())
  const categories: Ref<Categories> = ref({} as Categories)
  const categoriesLoaded: Ref<boolean> = ref(false)

  const loadCategories = async (): Promise<void> => {
    if (categoriesLoaded.value) return

    try {
      const response = await fetch('/data/categories.json')
      categories.value = await response.json()
      categoriesLoaded.value = true
    } catch (e) {
      console.error('Error loading categories:', e)
      throw e
    }
  }

  const updateOptions = (newOptions: Partial<GameOptions>): void => {
    options.value = { ...options.value, ...newOptions }
  }

  const resetToDefaults = (): void => {
    options.value = { ...DEFAULT_OPTIONS }
    saveToStorage()
  }

  // Guardar en localStorage
  const saveToStorage = (): void => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(options.value))
    } catch (e) {
      console.error('Error saving config to localStorage:', e)
    }
  }

  // Watcher para guardar automáticamente cuando cambien las opciones
  watch(options, () => {
    saveToStorage()
  }, { deep: true })

  return {
    options,
    categories,
    categoriesLoaded,
    loadCategories,
    updateOptions,
    resetToDefaults
  }
})
